import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "candidate-page candidate-subjects" }
const _hoisted_2 = { class: "candidate-container" }
const _hoisted_3 = { class: "candidate-page-head candidate-page-header" }
const _hoisted_4 = { class: "candidate-title-back" }
const _hoisted_5 = { class: "round-summary" }
const _hoisted_6 = { class: "round-number" }
const _hoisted_7 = { class: "round-progress-copy" }
const _hoisted_8 = {
  class: "subject-grid",
  "aria-label": "考试科目列表"
}
const _hoisted_9 = { class: "subject-card-top" }
const _hoisted_10 = {
  key: 0,
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_11 = {
  key: 1,
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_12 = {
  key: 2,
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
}
const _hoisted_13 = { class: "subject-rules" }
const _hoisted_14 = { class: "subject-result" }
const _hoisted_15 = { class: "subject-actions" }

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { $enum } from '@/global/index'
import { createRandomWind, getWindDirection, getWindSpeed } from '@/utils/index'
import CandidatePromptDialog from '@/components/candidate/CandidatePromptDialog.vue'
import {
  VkButton,
  VkCard,
  VkEmpty,
  VkProgress,
  VkTag,
} from '@/components/candidate/ui/index'
import {
  getPilotAssessmentRoundDetail,
  getSceneInfoBySceneId,
  getSecondaryPageData,
  getUserTrainingSetting,
  PilotAssessmentExamTimeStatus,
  PilotAssessmentRoundDetail,
  PilotAssessmentRoundSubject,
  StartPilotAssessmentSubjectParams,
  StartPilotAssessmentSubjectResult,
  startPilotAssessmentSubject,
} from '@/service'

interface PilotAssessmentTrainingContext {
  roundSubjectId?: string
  trainingId: string
  sceneId: string
  airLineId?: string | null
  stationId?: string | null
  subjectStatus?: string | number
  sessionId: string
  roundId: string
  subjectCode: string
}

interface ExamSceneInfo {
  id: string | number
  courseTypeId: string | number
  sceneCategoryId?: string | number
  heightLimit?: number
  scenePictureUrl?: string
  pictureUrl?: string
  uavNestId?: string | number | null
  airLineId?: string | number | null
  stationId?: string | number | null
  difficultyType?: number
  resourceType?: string | number | null
  resourceFileList?: unknown[]
  [key: string]: unknown
}

interface UavSpecificationInfo {
  id: string | number
  flyTime?: number
  maxZoom?: number
  maxHolderPitchRotation?: number
  minHolderPitchRotation?: number
  [key: string]: unknown
}

const TRAINING_CONTEXT_KEY = 'pilotAssessmentTrainingContext'

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

function getWindSpeedRange(windPower: unknown): string {
  const level = Number(windPower)
  const normalizedLevel = Number.isInteger(level) && level >= 0 && level <= 5 ? level : 0
  return getWindSpeed(normalizedLevel).value.replace('~', ',')
}

const route = useRoute()
const router = useRouter()
const store = useStore()
const loading = ref(true)
const errorMessage = ref('')
const round = ref<PilotAssessmentRoundDetail | null>(null)
const subjectActionCode = ref('')
const examEndedDialogVisible = ref(false)
let detailRequestSequence = 0

const progressPercentage = computed(() => {
  const completed = Number(round.value?.completedSubjectCount || 0)
  const total = Number(round.value?.totalSubjectCount || 0)
  return total > 0 ? Math.round((completed / total) * 100) : 0
})

function normalizedRoundStatus(): string {
  const status = round.value?.roundStatus
  if (status === 0) return 'IN_PROGRESS'
  if (status === 1) return 'COMPLETED'
  if (status === 2) return 'INCOMPLETE'
  return String(status || '').trim().toUpperCase()
}

function normalizedSubjectStatus(subject: PilotAssessmentRoundSubject): string {
  const status = subject.subjectStatus
  const numericStatuses: Record<string, string> = {
    '0': 'NOT_STARTED',
    '1': 'STARTING',
    '2': 'IN_PROGRESS',
    '3': 'PASSED',
    '4': 'FAILED',
    '5': 'INCOMPLETE',
  }
  return numericStatuses[String(status)] || String(status || '').trim().toUpperCase()
}

function isTerminal(subject: PilotAssessmentRoundSubject): boolean {
  const status = normalizedSubjectStatus(subject)
  return status === 'PASSED' || status === 'FAILED' || status === 'INCOMPLETE'
}

function statusMeta(subject: PilotAssessmentRoundSubject): {
  label: string
  theme: 'default' | 'primary' | 'success' | 'warning' | 'danger'
} {
  const status = normalizedSubjectStatus(subject)
  if (status === 'STARTING' || status === 'IN_PROGRESS') {
    return { label: '进行中', theme: 'primary' }
  }
  if (status === 'PASSED') return { label: '已通过', theme: 'success' }
  if (status === 'FAILED') return { label: '未通过', theme: 'danger' }
  if (status === 'INCOMPLETE') return { label: '未完成', theme: 'danger' }
  return { label: '未开始', theme: 'warning' }
}

function scoreText(subject: PilotAssessmentRoundSubject): string {
  if (subject.score === null || subject.score === undefined || subject.score === '') {
    return '无结果'
  }
  return `${subject.score} 分`
}

function canEnterSubject(subject: PilotAssessmentRoundSubject): boolean {
  return (
    normalizedRoundStatus() === 'IN_PROGRESS' &&
    !isTerminal(subject) &&
    subjectActionCode.value === ''
  )
}

function subjectActionLabel(subject: PilotAssessmentRoundSubject): string {
  return Number(subject.subjectStatus) > 2 ? '本考试科目已完成' : '开始考试'
}

async function loadRoundDetail() {
  const requestSequence = ++detailRequestSequence
  const roundId = String(route.query.roundId || '')
  if (!roundId) {
    round.value = null
    loading.value = false
    errorMessage.value = '缺少考试轮次信息'
    return
  }
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await getPilotAssessmentRoundDetail({ roundId })
    if (requestSequence !== detailRequestSequence) return
    if (response.code != $enum.EnumResult.SUCCESS || !response.data) {
      throw new Error(response.message || '考试科目加载失败')
    }
    round.value = {
      ...response.data,
      subjects: Array.isArray(response.data.subjects) ? response.data.subjects : [],
    }
    examEndedDialogVisible.value =
      response.data.examTimeStatus === PilotAssessmentExamTimeStatus.ENDED
  } catch (error) {
    if (requestSequence !== detailRequestSequence) return
    round.value = null
    examEndedDialogVisible.value = false
    errorMessage.value = error instanceof Error ? error.message : '考试科目加载失败'
  } finally {
    if (requestSequence === detailRequestSequence) loading.value = false
  }
}

function normalizeId(value: string | number, fieldName: string): string {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) {
    throw new Error(`${fieldName} 返回值存在精度丢失，请联系后端改为字符串返回`)
  }
  return String(value)
}

function buildStartParams(
  subject: PilotAssessmentRoundSubject,
  overrides: Partial<StartPilotAssessmentSubjectParams> = {},
): StartPilotAssessmentSubjectParams {
  const currentUavInfo = store.state.persistence?.currentUavInfo || {}
  const settings = store.state.persistence?.userTrainSettingInfo || {}
  const uavSpecificationId =
    overrides.uavSpecificationId || currentUavInfo.uavId || settings.uavSpId
  if (!uavSpecificationId) {
    throw new Error('缺少无人机型号配置，请先完成训练参数配置后再试')
  }

  const roundId = round.value?.roundId || String(route.query.roundId || '')
  if (!roundId) {
    throw new Error('缺少考试轮次信息，无法开始考试')
  }
  const params: StartPilotAssessmentSubjectParams = {
    roundId: normalizeId(roundId, 'roundId'),
    subjectCode: subject.subjectCode,
    uavSpecificationId: normalizeId(uavSpecificationId, 'uavSpecificationId'),
  }
  const windPowerValue = Object.prototype.hasOwnProperty.call(overrides, 'windPower')
    ? overrides.windPower
    : settings.windPower
  if (windPowerValue !== null && windPowerValue !== undefined) {
    const windPower = Number(windPowerValue)
    if (Number.isFinite(windPower)) params.windPower = windPower
  }
  const windDirectionValue = Object.prototype.hasOwnProperty.call(overrides, 'windDirection')
    ? overrides.windDirection
    : settings.windDirection
  if (windDirectionValue !== null && windDirectionValue !== undefined) {
    params.windDirection = String(windDirectionValue)
  }
  if (overrides.difficultyType !== null && overrides.difficultyType !== undefined) {
    params.difficultyType = Number(overrides.difficultyType)
  }
  return params
}

async function requestStartSubject(
  subject: PilotAssessmentRoundSubject,
  overrides: Partial<StartPilotAssessmentSubjectParams> = {},
): Promise<StartPilotAssessmentSubjectResult> {
  const response = await startPilotAssessmentSubject(buildStartParams(subject, overrides))
  if (
    response.code != $enum.EnumResult.SUCCESS ||
    !response.data?.roundSubjectId ||
    !response.data?.trainingId ||
    !response.data?.sceneId
  ) {
    throw new Error(response.message || '开始考试科目失败')
  }
  const result: StartPilotAssessmentSubjectResult = {
    ...response.data,
    roundSubjectId: normalizeId(response.data.roundSubjectId, 'roundSubjectId'),
    trainingId: normalizeId(response.data.trainingId, 'trainingId'),
    sceneId: normalizeId(response.data.sceneId, 'sceneId'),
    airLineId:
      response.data.airLineId == null
        ? null
        : normalizeId(response.data.airLineId, 'airLineId'),
    stationId:
      response.data.stationId == null
        ? null
        : normalizeId(response.data.stationId, 'stationId'),
  }
  cacheTrainingContext(subject, result)
  return result
}

function cacheTrainingContext(
  subject: PilotAssessmentRoundSubject,
  result: {
    roundSubjectId?: string | number
    trainingId: string | number
    sceneId: string | number
    airLineId?: string | number | null
    stationId?: string | number | null
    subjectStatus?: string | number
  },
) {
  const context: PilotAssessmentTrainingContext = {
    roundSubjectId: result.roundSubjectId
      ? normalizeId(result.roundSubjectId, 'roundSubjectId')
      : undefined,
    trainingId: normalizeId(result.trainingId, 'trainingId'),
    sceneId: normalizeId(result.sceneId, 'sceneId'),
    airLineId:
      result.airLineId == null ? null : normalizeId(result.airLineId, 'airLineId'),
    stationId:
      result.stationId == null ? null : normalizeId(result.stationId, 'stationId'),
    subjectStatus: result.subjectStatus,
    sessionId: String(round.value?.sessionId || route.params.sessionId || ''),
    roundId: String(round.value?.roundId || route.query.roundId || ''),
    subjectCode: subject.subjectCode,
  }
  sessionStorage.setItem(TRAINING_CONTEXT_KEY, JSON.stringify(context))
}

async function enterSubject(subject: PilotAssessmentRoundSubject) {
  if (!canEnterSubject(subject)) return
  subjectActionCode.value = subject.subjectCode
  try {
    if (!subject.sceneTypeId) {
      throw new Error('当前考试科目缺少场景类型，无法开始考试')
    }
    const commonParams = {
      userId: store.state.persistence?.userInfo?.userId,
      tenantId: store.state.persistence?.currentTenantId,
      sceneTypeId: subject.sceneTypeId,
    }

    const settingResponse = await getUserTrainingSetting(commonParams)
    if (settingResponse?.code != $enum.EnumResult.SUCCESS) {
      throw new Error(settingResponse?.message || '用户训练设置加载失败')
    }
    const settings = (settingResponse?.data || {}) as Record<string, any>

    const secondaryResponse = await getSecondaryPageData(commonParams)
    if (secondaryResponse?.code != $enum.EnumResult.SUCCESS || !secondaryResponse?.data) {
      throw new Error(secondaryResponse?.message || '无人机型号数据加载失败')
    }
    const secondaryData = secondaryResponse.data as {
      sceneUavTypeVOList?: UavSpecificationInfo[]
    }
    const uavList = Array.isArray(secondaryData.sceneUavTypeVOList)
      ? secondaryData.sceneUavTypeVOList
      : []
    if (!uavList.length) {
      throw new Error('当前考试科目没有可用的无人机型号')
    }
    const selectedUav =
      uavList.find((item) => String(item.id) === String(settings.uavSpId || '')) ||
      uavList[0]
    store.commit('persistence/setCurrentUavInfo', {
      ...(store.state.persistence?.currentUavInfo || {}),
      uavId: selectedUav.id,
      flyTime: selectedUav.flyTime,
      maxZoom: selectedUav.maxZoom,
      maxHolderPitch: selectedUav.maxHolderPitchRotation,
      minHolderPitch: selectedUav.minHolderPitchRotation,
    })

    let windPower = settings.windPower ?? 0
    let windDirection = settings.windDirection || getWindDirection(0)
    if (subject.subjectCode === 'BASE_FLIGHT') {
      const direction = createRandomWind().direction
      store.commit('viewData/setExamWindDirection', direction)
      windDirection = getWindDirection(direction)
      windPower = null
    }

    const startResult = await requestStartSubject(subject, {
      uavSpecificationId: selectedUav.id,
      difficultyType: settings.difficultyType ?? undefined,
      windPower: windPower ?? undefined,
      windDirection: windDirection ?? undefined,
    })
    const trainingId = normalizeId(startResult.trainingId, 'trainingId')
    const sceneId = normalizeId(startResult.sceneId, 'sceneId')
    const authoritativeStationId =
      startResult.stationId == null
        ? null
        : normalizeId(startResult.stationId, 'stationId')
    const authoritativeAirLineId =
      startResult.airLineId == null
        ? null
        : normalizeId(startResult.airLineId, 'airLineId')

    const sceneResponse = await getSceneInfoBySceneId({ id: sceneId })
    if (sceneResponse?.code != $enum.EnumResult.SUCCESS || !sceneResponse?.data) {
      throw new Error(sceneResponse?.message || '权威考试场景加载失败')
    }
    const scene = {
      ...(sceneResponse.data as ExamSceneInfo),
      id: sceneId,
    }
    if (!scene.courseTypeId) {
      throw new Error('权威考试场景信息不完整')
    }
    const courseTypeId = Number(scene.courseTypeId)
    if (courseTypeId !== 1 && courseTypeId !== 3) {
      throw new Error(`暂不支持课程类型 ${scene.courseTypeId}`)
    }

    const stationId = scene.stationId ?? authoritativeStationId ?? null
    const airLineId = scene.airLineId ?? authoritativeAirLineId ?? null
    if (courseTypeId === 3 && (!stationId || !airLineId)) {
      throw new Error('考试场景的航线或站点配置缺失，请联系管理员')
    }
    const authoritativeScene: ExamSceneInfo = {
      ...scene,
      id: sceneId,
      stationId,
      airLineId,
      uavNestId: scene.uavNestId ?? null,
      resourceFileList: scene.resourceFileList || [],
    }
    const sceneImageUrl =
      authoritativeScene.scenePictureUrl || authoritativeScene.pictureUrl || ''
    const nestId = authoritativeScene.uavNestId ?? null
    const resourceFileList = authoritativeScene.resourceFileList || []
    const resourceTypeId = authoritativeScene.resourceType ?? settings.resourceTypeId ?? null

    const currentSettings = store.state.persistence?.userTrainSettingInfo || {}
    store.commit('persistence/setUserTrainingSetting', {
      ...currentSettings,
      ...settings,
      uavSpId: selectedUav.id,
      resourceTypeId,
      currentStationId: stationId,
    })
    store.commit('persistence/setCurrentResourceId', resourceTypeId)

    const currentUavInfo = store.state.persistence?.currentUavInfo || {}
    store.commit('persistence/setCurrentUavInfo', {
      ...currentUavInfo,
      uavId: selectedUav.id,
      flyTime: selectedUav.flyTime,
      maxZoom: selectedUav.maxZoom,
      maxHolderPitch: selectedUav.maxHolderPitchRotation,
      minHolderPitch: selectedUav.minHolderPitchRotation,
      nestId,
      trainingId,
      heightLimit: authoritativeScene.heightLimit,
      parentName: '考评',
      sceneImageUrl,
      sceneInfo: authoritativeScene,
      resourceFileList,
      windSpeedIndex: '',
      windDirectionIndex: '',
    })
    store.commit('viewData/setFlyReplayData', {
      ...store.state.viewData.flyReplayData,
      enterPageMode: 1,
    })
    store.commit('persistence/setStationId', stationId)
    await router.push(
      courseTypeId === 1
        ? '/pilot-assesment/flight/exam'
        : '/pilot-assesment/flight/transmit',
    )
  } catch {
    ElMessage.error({
      message: '开始考试失败，请重试',
      duration: 1500,
    })
  } finally {
    subjectActionCode.value = ''
  }
}

function goExam() {
  router.push({
    name: 'CandidateExam',
    params: { sessionId: String(round.value?.sessionId || route.params.sessionId || '') },
  })
}

function handleExamEndedConfirm() {
  sessionStorage.removeItem(TRAINING_CONTEXT_KEY)
  router.replace({ name: 'CandidateHome' })
}

function subjectIconType(subjectCode: string): 'drone' | 'route' | 'tower' {
  if (subjectCode === 'BASE_FLIGHT') return 'drone'
  if (subjectCode === 'DISTRIBUTION_INSPECTION') return 'route'
  return 'tower'
}

function subjectIconClass(subjectCode: string): 'blue-bg' | 'orange-bg' | 'violet-bg' {
  if (subjectCode === 'BASE_FLIGHT') return 'blue-bg'
  if (subjectCode === 'DISTRIBUTION_INSPECTION') return 'orange-bg'
  return 'violet-bg'
}

watch(
  () => [route.params.sessionId, route.query.roundId],
  () => loadRoundDetail(),
  { immediate: true },
)

onBeforeUnmount(() => {
  detailRequestSequence += 1
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(VkButton), {
            class: "candidate-back-button",
            variant: "text",
            tone: "default",
            onClick: goExam
          }, {
            default: _withCtx(() => _cache[1] || (_cache[1] = [
              _createTextVNode(" ← 返回考试页面 ")
            ])),
            _: 1
          }),
          _cache[2] || (_cache[2] = _createElementVNode("div", null, [
            _createElementVNode("h1", null, "考试科目"),
            _createElementVNode("p", null, "可自由选择未开始科目，每科在本轮仅有一次考试机会。")
          ], -1))
        ])
      ]),
      (loading.value)
        ? (_openBlock(), _createBlock(_unref(VkCard), {
            key: 0,
            class: "candidate-card candidate-empty"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VkEmpty), {
                size: "large",
                description: "考试科目加载中..."
              })
            ]),
            _: 1
          }))
        : (errorMessage.value)
          ? (_openBlock(), _createBlock(_unref(VkCard), {
              key: 1,
              class: "candidate-card candidate-empty"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(VkEmpty), {
                  size: "large",
                  description: errorMessage.value
                }, null, 8, ["description"])
              ]),
              _: 1
            }))
          : (round.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createVNode(_unref(VkCard), { class: "candidate-card round-banner" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, "#" + _toDisplayString(round.value.roundNo), 1),
                      _createElementVNode("div", null, [
                        _createElementVNode("h2", null, "第 " + _toDisplayString(round.value.roundNo) + " 次考试", 1),
                        _createElementVNode("p", null, _toDisplayString(round.value.sessionName), 1)
                      ])
                    ]),
                    _createElementVNode("div", null, [
                      _createElementVNode("div", _hoisted_7, [
                        _cache[3] || (_cache[3] = _createElementVNode("span", null, "本轮完成进度", -1)),
                        _createElementVNode("strong", null, _toDisplayString(round.value.completedSubjectCount) + " / " + _toDisplayString(round.value.totalSubjectCount), 1)
                      ]),
                      _createVNode(_unref(VkProgress), {
                        kind: "line",
                        size: "medium",
                        tone: "primary",
                        percentage: progressPercentage.value,
                        "show-text": false
                      }, null, 8, ["percentage"])
                    ])
                  ]),
                  _: 1
                }),
                _createElementVNode("section", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(round.value.subjects, (subject) => {
                    return (_openBlock(), _createBlock(_unref(VkCard), {
                      key: subject.subjectCode,
                      class: _normalizeClass(["candidate-card subject-card", { 'is-completed': isTerminal(subject) }])
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_9, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["candidate-subject-icon", subjectIconClass(subject.subjectCode)])
                          }, [
                            (subjectIconType(subject.subjectCode) === 'drone')
                              ? (_openBlock(), _createElementBlock("svg", _hoisted_10, _cache[4] || (_cache[4] = [
                                  _createElementVNode("path", {
                                    d: "M8 12h8M12 8v8M5 7h3l2 3M19 7h-3l-2 3M5 17h3l2-3M19 17h-3l-2-3M3 5h4v4H3zM17 5h4v4h-4zM3 15h4v4H3zM17 15h4v4h-4z",
                                    fill: "none",
                                    stroke: "currentColor",
                                    "stroke-width": "1.7",
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round"
                                  }, null, -1)
                                ])))
                              : (subjectIconType(subject.subjectCode) === 'route')
                                ? (_openBlock(), _createElementBlock("svg", _hoisted_11, _cache[5] || (_cache[5] = [
                                    _createElementVNode("path", {
                                      d: "M5 5h5a3 3 0 0 1 0 6H8a3 3 0 0 0 0 6h11M5 5l2-2M5 5l2 2M19 17l-2-2M19 17l-2 2",
                                      fill: "none",
                                      stroke: "currentColor",
                                      "stroke-width": "1.7",
                                      "stroke-linecap": "round",
                                      "stroke-linejoin": "round"
                                    }, null, -1)
                                  ])))
                                : (_openBlock(), _createElementBlock("svg", _hoisted_12, _cache[6] || (_cache[6] = [
                                    _createElementVNode("path", {
                                      d: "M12 3l5 18M12 3L7 21M9 10h6M8 15h8M5 21h14",
                                      fill: "none",
                                      stroke: "currentColor",
                                      "stroke-width": "1.7",
                                      "stroke-linecap": "round"
                                    }, null, -1)
                                  ])))
                          ], 2),
                          _createVNode(_unref(VkTag), {
                            theme: statusMeta(subject).theme,
                            variant: "light",
                            shape: "round"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(statusMeta(subject).label), 1)
                            ]),
                            _: 2
                          }, 1032, ["theme"])
                        ]),
                        _createElementVNode("h2", null, _toDisplayString(subject.subjectName), 1),
                        _cache[11] || (_cache[11] = _createElementVNode("p", null, "本轮仅有一次机会，异常结束时本考试科目记 0 分。", -1)),
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("div", null, [
                            _cache[7] || (_cache[7] = _createElementVNode("span", null, "总分", -1)),
                            _createElementVNode("strong", null, _toDisplayString(subject.fullScore) + " 分", 1)
                          ]),
                          _createElementVNode("div", null, [
                            _cache[8] || (_cache[8] = _createElementVNode("span", null, "通过线", -1)),
                            _createElementVNode("strong", null, _toDisplayString(subject.passScore) + " 分", 1)
                          ]),
                          _createElementVNode("div", null, [
                            _cache[9] || (_cache[9] = _createElementVNode("span", null, "科目权重", -1)),
                            _createElementVNode("strong", null, _toDisplayString(subject.weightPercent) + "%", 1)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_14, [
                          _cache[10] || (_cache[10] = _createElementVNode("span", null, "考试结果", -1)),
                          _createElementVNode("strong", null, _toDisplayString(scoreText(subject)), 1)
                        ]),
                        _createElementVNode("div", _hoisted_15, [
                          _createVNode(_unref(VkButton), {
                            class: "subject-action",
                            block: "",
                            size: "large",
                            variant: isTerminal(subject) ? 'outline' : 'solid',
                            tone: isTerminal(subject) ? 'default' : 'primary',
                            disabled: !canEnterSubject(subject),
                            loading: subjectActionCode.value === subject.subjectCode,
                            onClick: ($event: any) => (enterSubject(subject))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(subjectActionLabel(subject)), 1)
                            ]),
                            _: 2
                          }, 1032, ["variant", "tone", "disabled", "loading", "onClick"])
                        ])
                      ]),
                      _: 2
                    }, 1032, ["class"]))
                  }), 128))
                ])
              ], 64))
            : _createCommentVNode("", true)
    ]),
    _createVNode(CandidatePromptDialog, {
      modelValue: examEndedDialogVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((examEndedDialogVisible).value = $event)),
      title: "提示",
      message: "考试时间已结束，系统已自动终止当次考试。",
      "confirm-text": "确认",
      "cancel-text": "取消",
      "show-cancel": false,
      "show-close": false,
      onConfirm: handleExamEndedConfirm
    }, null, 8, ["modelValue"])
  ]))
}
}

})